import { useContext, useEffect, useState } from 'react';

import { toCurrency } from '@Helpers';
import { motion } from 'framer-motion';
import mixpanel from 'mixpanel-browser';

import { ProductCardFeatures } from '@Components/ProductCardFeatures/ProductCardFeatures';
import StarLabel from '@Components/StarLabel/StarLabel';
import { getProductType, getRateType } from '@Shared/helpers/offer';
import { ApplicationContext } from '@src/app/contexts/Application';
import { useCallBookingVariant } from '@src/app/hooks/useCallBookingVariant';
import { Offer, Product } from '@src/app/interfaces/offer';
import { KAMELEOON_FEATURE } from '@src/app/utils/kameleoon';
import { toPercentage } from '@src/shared/helpers/numbers';

import ChevronIcon from '../Icons/Chevron';
import RadioSelected from '../Icons/RadioSelected';
import RadioUnselected from '../Icons/RadioUnselected';
import Legal from '../Legal/Legal';

interface OfferCardProps {
    offer: Offer;
    onSelect?: () => void;
    selectable?: boolean;
    selected: boolean;
    expanded: boolean;
    toggle: () => void;
    starLabel?: string;
    className?: string;
    hasDeclinedOffer: boolean;
}

const OfferCard = ({
    offer,
    onSelect,
    selectable,
    selected,
    expanded,
    toggle,
    starLabel,
    className,
    hasDeclinedOffer,
}: OfferCardProps) => {
    const [showMoreInfo, setShowMoreInfo] = useState(false);
    const productType = getProductType(offer);
    const callBookingVariant = useCallBookingVariant();
    const { applicationId } = useContext(ApplicationContext);

    useEffect(() => {
        setShowMoreInfo(expanded);
    }, [expanded]);

    const getLoanType = () => {
        switch (productType) {
            case Product.HELOC:
                return 'Flexible drawdowns';
            case Product.TwoYearFixedWithEarlyCharges:
                return 'Shortest fixed period';
            case Product.FiveYearFixedWithEarlyCharges:
                return 'Lowest monthly payment';
            default:
                return '';
        }
    };

    const onCardClick = () => {
        if (!selected) {
            mixpanel.track('Click offer card', {
                'Product selected': offer.product,
                experiment_name: callBookingVariant
                    ? KAMELEOON_FEATURE.call_booking_redesign
                    : null,
                variant: callBookingVariant || null,
                application_id: applicationId,
            });
            onSelect?.();
        }
    };

    const renderButton = (isSelected: boolean) => {
        return isSelected ? (
            <RadioSelected />
        ) : (
            <RadioUnselected onClick={onCardClick} />
        );
    };

    const handleToggle = () => {
        if (window.innerWidth >= 1024) {
            toggle();
        } else {
            setShowMoreInfo((prev) => !prev);
        }
    };

    return (
        <motion.div
            data-testid='offer-card'
            initial={false}
            animate={{
                scale: selected && window.innerWidth >= 1024 ? 1.05 : 1,
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={`
                mb-4 py-6 px-6 rounded-t-2xl rounded-bl-2xl rounded-br
                w-full max-w-330 lg:max-w-none lg:mr-6 flex flex-col bg-almond-200
                ${
                    selected
                        ? 'shadow-offerSelected border-b-4 border-midnight-500'
                        : 'shadow-offer'
                }
                ${className}
            `}
        >
            <div
                className={`flex flex-col flex-grow ${
                    !starLabel && selectable ? '-mt-2 lg:-mt-6' : ''
                }`}
            >
                <div onClick={onCardClick} className='cursor-pointer'>
                    {selectable && (
                        <div className='pb-3 lg:h-8'>
                            {starLabel && <StarLabel text={starLabel} />}
                        </div>
                    )}
                    <div className='flex justify-between items-center pb-5'>
                        <div className='leading-120%'>
                            <span className='text-28 font-sora-bold'>{`£${toCurrency(
                                offer.initialPayment,
                            )}`}</span>
                            <span className='text-xl'>/month</span>
                            {hasDeclinedOffer && (
                                <p className='text-xl font-bold'>
                                    {getLoanType()}
                                </p>
                            )}
                        </div>
                        {selectable && renderButton(selected)}
                    </div>
                    <ProductCardFeatures offer={offer} rateFeatureType='aprc' />
                    {showMoreInfo && (
                        <div className='pb-3 text-sm'>
                            <div className='flex justify-between bg-monochrome-100 p-3 rounded-t mb-0.5'>
                                <p>Rate type</p>
                                <p className='font-sora-bold text-midnight-200'>
                                    {getRateType(offer)}
                                </p>
                            </div>
                            <div className='flex justify-between bg-monochrome-100 p-3 rounded-t mb-0.5'>
                                <p>Product</p>
                                <p className='font-sora-bold text-midnight-200'>
                                    {offer.family}
                                </p>
                            </div>
                            <div className='flex justify-between bg-monochrome-100 p-3 rounded-t mb-0.5'>
                                <p>Total cost of loan</p>
                                <p className='font-sora-bold text-midnight-200'>{`£${toCurrency(
                                    offer.totalAmountRepaid,
                                    2,
                                    2,
                                )}`}</p>
                            </div>
                            <div className='flex justify-between bg-monochrome-100 p-3 rounded-t mb-4'>
                                <p>Interest rate</p>
                                <p className='font-sora-bold text-midnight-200'>
                                    {toPercentage(offer.initialRate)}
                                </p>
                            </div>

                            <Legal className='leading-120%' offer={offer} />
                        </div>
                    )}
                </div>
            </div>
            <div
                onClick={handleToggle}
                className='w-32 text-xs mt-4 font-bold justify-center bg-midnight-500 text-monochrome-100 items-center flex rounded-2xl cursor-pointer'
            >
                <span className='pr-1'>
                    {!showMoreInfo ? 'Quote details' : 'Hide details'}
                </span>
                <ChevronIcon
                    isRotated={showMoreInfo}
                    height='12px'
                    width='12px'
                    color='white'
                />
            </div>
        </motion.div>
    );
};

export default OfferCard;
